// import components
import { MultiSelect } from 'components/shared';
import VerticalToolbarActionWrapper from 'components/shared/ToolbarSet/components/VerticalToolbarActionWrapper';

// import external libraries
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// import styles
import {
  BacklogControlStack,
  BacklogFieldContainer,
  BacklogPopoverTitle,
  BacklogToolbarField
} from '../../Backlog.styles';

// import types
import type { AutocompleteRenderInputParams } from '@mui/material';
import type { FilterOption } from '../../Backlog.types';
import type { BacklogFiltersProps } from './BacklogFilters.types';

/**
 * Sits in the toolbar's beforeDatePicker slot: everything that narrows *which* tasks are listed.
 * Activity and care receiver, both the same shared MultiSelect over options taken from the response.
 */
const BacklogFilters = ({
  activityOptions,
  selectedActivityOptions,
  onActivityCodesChange,
  careReceiverOptions,
  selectedCareReceiverOptions,
  onCareReceiverIdsChange,
  isRightPositioned
}: BacklogFiltersProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  // The count wording sits at the root of the namespace, shared with the app's other compact
  // multi-selects, so it needs a second hook without the backlog prefix.
  const { t: tCommon } = useTranslation('common');

  /**
   * Without this the field looked untouched after picking something: MultiSelect defaults renderTags to
   * () => '' and hardcodes limitTags to 0, so nothing at all is drawn for a selection.
   *
   * Returning a string rather than elements is what makes it show — MUI only applies limitTags when
   * renderTags hands back an array, so a string slips past that 0. A count rather than the names: it is
   * one length whatever is picked, and it is the same summary the plan unit selector shows through
   * getLimitTagsText.
   */
  const renderTags = (selected: FilterOption[]) =>
    `${selected.length} ${tCommon('selected').toLowerCase()}`;

  /**
   * The plan unit selector's own toolbar field, so these read as the control beside them in the
   * berichten toolbar rather than as form inputs: borderless, no floating label, its value and a caret,
   * and a rounded hover fill. isToolbar is what strips the border and the underline, which is why the
   * variant has to be standard — that styling targets the underline classes.
   *
   * It keeps its label, held shrunk so it always sits above the value rather than only once something is
   * picked. That also names the field: MUI wires the label's id into the input's aria-labelledby, so no
   * aria-label of our own is needed — which is why params.InputLabelProps is merged rather than replaced.
   */
  const renderInputFor =
    (label: string) => (params: AutocompleteRenderInputParams) => (
      <BacklogToolbarField
        {...params}
        label={label}
        isToolbar
        variant="standard"
        size="small"
        slotProps={{
          inputLabel: { ...params.InputLabelProps, shrink: true }
        }}
      />
    );

  const renderFilter = (
    label: string,
    options: FilterOption[],
    selectedOptions: FilterOption[],
    onChange: (ids: string[]) => void
  ) => (
    <BacklogFieldContainer key={label}>
      <MultiSelect<FilterOption>
        renderInput={renderInputFor(label)}
        renderTags={renderTags}
        options={options}
        selectedOptions={selectedOptions}
        onChange={(picked) => onChange(picked.map(({ id }) => id))}
        // Render the options list inside the toolbar popover rather than portaled to the body. Portaled,
        // the list is positioned against the popover before it has settled, so it recomputes against the
        // popover-at-origin and jumps to the top-left corner on each selection. Kept inline, it shares the
        // popover's coordinate space and stays anchored under its field.
        disablePortal
      />
    </BacklogFieldContainer>
  );

  const fields = (
    <>
      {renderFilter(
        t('filters.activity'),
        activityOptions,
        selectedActivityOptions,
        onActivityCodesChange
      )}
      {renderFilter(
        t('filters.careReceiver'),
        careReceiverOptions,
        selectedCareReceiverOptions,
        onCareReceiverIdsChange
      )}
    </>
  );

  // One green dot per filter that is narrowing the list — activity and care receiver, so at most two.
  const activeFilterCount =
    (selectedActivityOptions.length > 0 ? 1 : 0) +
    (selectedCareReceiverOptions.length > 0 ? 1 : 0);

  // Always a single icon that opens both filters stacked in a popper — the sliders icon of the flex
  // match toolbar. Across the top the popper drops down under the icon; docked to the side it opens
  // into the page to its left.
  return (
    <VerticalToolbarActionWrapper
      iconType="sliders"
      title={t('filters.title')}
      placement={isRightPositioned ? 'left' : 'bottom-start'}
      tooltipPlacement={isRightPositioned ? 'left' : 'bottom'}
      badge={{ isActive: activeFilterCount > 0, content: activeFilterCount }}
    >
      <BacklogControlStack>
        <BacklogPopoverTitle variant="subtitle1">
          {t('filters.title')}
        </BacklogPopoverTitle>
        {fields}
      </BacklogControlStack>
    </VerticalToolbarActionWrapper>
  );
};

export default memo(BacklogFilters);
